import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { FormGroupDirective, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { AlertButtonColor } from "@obg/mat.ui.core";
import { MultiSessionLimitAction, MultiSessionLimitResponse, MultiSessionLimitSelector } from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	LimitsReminderAction,
	LimitsReminderSelector,
	LimitsSummary,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { zhhSessionLimitValidator } from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { millisecondsToHours, millisecondsToMinutes } from "date-fns";
import { merge, Observable, Subscription } from "rxjs";
import { distinctUntilChanged, tap } from "rxjs/operators";

import { ResponsibleGamingFormService } from "../../containers/responsible-gaming-form.service";

import { ZhhSessionLimitSubmitType, ZhhSessionLimitTemplate } from "./zhh-session-limit.container.model";

@Component({
	selector: "obg-m-zhh-session-limit-container",
	templateUrl: "./zhh-session-limit.container.html",
	styleUrls: ["./zhh-session-limit.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class ZhhSessionLimitContainer implements OnInit, OnDestroy {
	readonly MAX_DAILY_SESSION_LIMIT_VALUE = 24;
	readonly REMOVED_DAILY_SESSION_LIMIT_VALUE = 0;

	@HostBinding("class.obg-m-zhh-session-limit") hostClass = true;
	@ViewChild("formDirective") formDirective: FormGroupDirective | undefined;

	isInfoVisible = false;
	valueRemoved = false;
	isSettingBusy = false;
	isLimitSet = false;
	showSkipButton = true;
	sessionLimitFormGroup: UntypedFormGroup;
	placeholder = "multi-session-limit.placeholder";
	inputInformation = "";
	currentSessionLimit: ZhhSessionLimitTemplate | undefined;
	upcomingSessionLimit: ZhhSessionLimitTemplate | undefined;
	sessionLimits$: Observable<LimitsSummary | undefined> | undefined;
	millisecondsToHours = millisecondsToHours;
	private data$$: Subscription | undefined;
	private isFormDirty$$: Subscription | undefined;

	constructor(
		private store: Store,
		private formBuilder: UntypedFormBuilder,
		private changeDetector: ChangeDetectorRef,
		private alertService: AbstractTranslatableAlertService,
		private multiSessionLimitAction: MultiSessionLimitAction,
		private multiSessionLimitSelector: MultiSessionLimitSelector,
		private responsibleGamingFormService: ResponsibleGamingFormService
	) {
		this.sessionLimitFormGroup = this.formBuilder.group({
			limit: this.formBuilder.control("", [zhhSessionLimitValidator]),
		});
	}

	ngOnInit() {
		this.store.dispatch(this.multiSessionLimitAction.get());

		this.sessionLimits$ = this.store.select(LimitsReminderSelector.getLimits);

		const sessionLimits$ = this.store.pipe(
			select(this.multiSessionLimitSelector.get()),
			ignoreNil(),
			tap((sessionLimit) => {
				this.currentSessionLimit = sessionLimit.current
					? this.mapSessionLimits(sessionLimit.current)
					: undefined;
				this.upcomingSessionLimit = sessionLimit.upcoming
					? this.mapSessionLimits(sessionLimit.upcoming)
					: undefined;
				this.showSkipButton = !this.currentSessionLimit?.dailySessionLimit;
				this.isLimitSet = sessionLimit.current?.dailySessionLimit !== this.REMOVED_DAILY_SESSION_LIMIT_VALUE;

				const limitValue = sessionLimit.upcoming
					? sessionLimit.upcoming.dailySessionLimit
					: sessionLimit.current?.dailySessionLimit;
				(this.valueRemoved =
					limitValue === this.REMOVED_DAILY_SESSION_LIMIT_VALUE ||
					limitValue === this.MAX_DAILY_SESSION_LIMIT_VALUE)
					? this.resetFormAndValidators()
					: this.sessionLimitFormGroup.controls.limit.setValue(limitValue);

				if (!this.isLimitSet) {
					this.placeholder = "multi-session-limit.placeholder-no-limit-set";
				}
			})
		);

		const sessionLimitValueChanges$ = this.sessionLimitFormGroup.controls.limit.valueChanges.pipe(
			tap((value: string) => {
				if (value !== null) {
					this.inputInformation = this.getInputInformation(Number(value));
				} else if (this.upcomingSessionLimit) {
					this.inputInformation = this.getInputInformation(
						Number(this.upcomingSessionLimit.dailySessionLimit)
					);
				}
			})
		);

		const isBusy$ = this.store.pipe(
			select(this.multiSessionLimitSelector.isBusy()),
			distinctUntilChanged(),
			tap((isBusy) => {
				if (!isBusy) {
					this.store.dispatch(LimitsReminderAction.fetchLimitsSummary(false));
				}
				this.isSettingBusy = isBusy;
			})
		);

		this.data$$ = merge(sessionLimits$, sessionLimitValueChanges$, isBusy$).subscribe(() =>
			this.changeDetector.markForCheck()
		);

		this.isFormDirty$$ = this.sessionLimitFormGroup.valueChanges.subscribe(() =>
			this.responsibleGamingFormService.sendIsFormDirty(this.sessionLimitFormGroup.dirty)
		);
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.isFormDirty$$?.unsubscribe();
	}

	generateAlertMessageText(submitType: ZhhSessionLimitSubmitType): string {
		if (submitType === "Undo") {
			return "undo-limit-confirmation-title";
		}
		if (this.currentSessionLimit?.dailySessionLimit) {
			if (
				submitType === "Remove" ||
				this.sessionLimitFormGroup.controls.limit.value > this.currentSessionLimit?.dailySessionLimit
			) {
				return "responsible-gaming.increase-limit-text";
			}
			return "responsible-gaming.decrease-limit-text";
		}
		return "responsible-gaming.confirmation-caption";
	}

	submitSessionLimit(submitType: ZhhSessionLimitSubmitType) {
		this.alertService.open(
			{
				id: "ZHH_SESSION_LIMIT_ALERT_ID",
				title: `multi-session-limit.${
					submitType === "Undo" ? "undo-limit-confirmation-title" : "confirmation-title"
				}`,
				message: this.generateAlertMessageText(submitType),
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				mustBeInteracted: true,
				buttons: [
					{
						text: "general.confirm",
						handler: () => {
							switch (submitType) {
								case "Remove":
									this.removeSessionLimit();
									break;
								case "Set":
									this.setSessionLimit(this.sessionLimitFormGroup.controls.limit.value);
									break;
								case "Undo":
									this.undoPendingChange();
									break;
							}
							this.isSettingBusy = true;
							this.changeDetector.markForCheck();
							this.sessionLimitFormGroup.markAsPristine();
						},
					},
					{
						text: "general.cancel",
						color: AlertButtonColor.Primary,
					},
				],
			},
			[TranslationGroup.Common]
		);
	}

	toggleInfoVisibility() {
		this.isInfoVisible = !this.isInfoVisible;
	}

	getHoursAndMinutesFromMillis(millis: number) {
		const hourString = Math.floor(millisecondsToHours(millis)).toLocaleString(undefined, {
			minimumIntegerDigits: 2,
		});
		const minuteString = (millisecondsToMinutes(millis) % 60).toLocaleString(undefined, {
			minimumIntegerDigits: 2,
		});
		return `${hourString}:${minuteString}`;
	}

	private setSessionLimit(dailyValue: number) {
		const MAX_WEEKLY_LIMIT = 168;
		const MAX_MONTHLY_LIMIT = 720;
		this.store.dispatch(
			this.multiSessionLimitAction.update({
				dailySessionLimit: dailyValue,
				weeklySessionLimit: MAX_WEEKLY_LIMIT,
				monthlySessionLimit: MAX_MONTHLY_LIMIT,
			})
		);
	}

	private undoPendingChange() {
		if (this.currentSessionLimit) {
			this.setSessionLimit(this.currentSessionLimit.dailySessionLimit);
		}
	}

	private removeSessionLimit() {
		if (this.currentSessionLimit) {
			this.store.dispatch(this.multiSessionLimitAction.remove());
		} else {
			this.setSessionLimit(this.MAX_DAILY_SESSION_LIMIT_VALUE);
		}
	}

	private mapSessionLimits(sessionLimit: MultiSessionLimitResponse) {
		return {
			dailySessionLimit: sessionLimit.dailySessionLimit
				? sessionLimit.dailySessionLimit
				: this.MAX_DAILY_SESSION_LIMIT_VALUE,
			activeFrom: sessionLimit.activeFrom,
		};
	}

	private resetFormAndValidators() {
		this.formDirective?.resetForm();
		this.placeholder = "multi-session-limit.not-set";
	}

	private getInputInformation(value: number) {
		if (!this.currentSessionLimit?.dailySessionLimit) {
			return "";
		}
		if (value === this.currentSessionLimit?.dailySessionLimit || this.upcomingSessionLimit?.dailySessionLimit) {
			if (this.upcomingSessionLimit) {
				return this.upcomingSessionLimit?.dailySessionLimit === this.MAX_DAILY_SESSION_LIMIT_VALUE
					? "multi-session-limit.upcoming-limit.daily.no-limit-set"
					: "multi-session-limit.upcoming-limit.daily";
			}
			return this.upcomingSessionLimit ? "multi-session-limit.upcoming-limit.daily" : "";
		}
		return value > this.currentSessionLimit?.dailySessionLimit
			? "multi-session-limit.increasing"
			: "multi-session-limit.lowering";
	}
}
